<template>
  <ErrorModal v-if="!isLoading && errorModalVisible" @close="$emit('close')" />
  <div v-else-if="isLoading" class="loading-wrapper">
    <BaseLoadingSpinner :with-overlay="false" />
  </div>
  <ApplicationContainerModal
    v-else
    v-esc-click="handleEscClick"
    @close="$emit('close')"
  >
    <template #modals>
      <CommentModal
        v-if="selectedComment"
        :name="selectedComment.name"
        :document-type="selectedComment.documentType"
        :content="selectedComment.content"
        @close="selectedComment = null"
      />
      <SubletNotesModal
        v-if="currentModal === 'NOTES'"
        :unread-notes="application.unreadNotes"
        :application-id="`${application.id}`"
        :read-only-mode="false"
        :application-landlord-id="application.landlordId"
        :application-type="application.applicationType"
        @close="currentModal = null"
        @readNotes="readNotes"
      />
      <SubletDocumentsModal
        v-if="currentModal === 'DOCUMENTS'"
        :application-id="`${application.id}`"
        :selected-name="applicantName"
        :single-docs-mode="!dualDocsMode"
        :current-tenant-name="currentTenants[0].name"
        :secondary-tenant-name="secondaryTenants[0].name"
        :files="application.files"
        :selected-type="selectedDocumentType"
        :selected-document-category-types="selectedDocumentCategoryTypes"
        :selected-applicant-id="selectedApplicantId"
        :has-scrive-document="!!application.scriveId"
        @close="
          currentModal = null;
          selectedApplicantId = null;
          selectedDocumentType = null;
          selectedDocumentCategoryTypes = [];
        "
      />

      <SubletApprovalModal
        v-if="currentModal === 'APPROVE'"
        :application-id="application.id"
        :initial-start-date="application.startDate"
        :initial-end-date="application.endDate"
        :rent-from="application.reason.rentFrom"
        :rent-to="application.reason.rentTo"
        @close="currentModal = null"
        @onApproved="leaveApplication"
      ></SubletApprovalModal>

      <SubletRejectModal
        v-if="currentModal === 'REJECT'"
        :application-id="application.id"
        @close="currentModal = null"
        @onRejected="leaveApplication"
      />

      <NotAssignedModal
        v-if="currentModal === 'USER_NOT_ASSIGNED'"
        @close="currentModal = null"
        @confirm="currentModal = 'REMOVE'"
      />

      <SubletRemoveModal
        v-if="currentModal === 'REMOVE'"
        :application-id="application.id"
        @close="currentModal = null"
        @onRemoved="leaveApplication"
      />

      <RestoreAhModal
        v-if="currentModal === 'RESTORE'"
        :application-id="application.id"
        @close="currentModal = null"
        @onRestored="leaveApplication"
      />

      <SubletCloseModal
        v-if="currentModal === 'CLOSE'"
        :application-id="application.id"
        @close="currentModal = null"
        @cancelled="leaveApplication"
      />
    </template>
    <template #header>
      <TabHeader
        :all-disabled="isAnimatingModalSize"
        :title="getReasonText"
        :subtitle="tabHeaderSubtitle"
        :application-status="application.landlordStatus"
        :show-help-button="false"
        :tabs="activeTabs"
        :is-archived="isArchived"
        :landlord-name="account?.managerId ? application.landlordName : ''"
        @tabClick="setTab"
        ><template v-if="ahAppsChains[application.id]" #chain-link
          ><LinkChain
            :id="ahAppsChains[application.id]"
            class="link-chain"
            :text="application.latestVersion ? 'Förlängning' : ''"
            :reverse="application.latestVersion === 0" /></template
      ></TabHeader>
    </template>
    <template #content>
      <template v-if="activeTab === 1">
        <ApplicationsRow two-columns>
          <AhOvApplicationCard
            :no-top-left-border="activeCurrentTenantIndex === 0"
            :no-top-right-border="
              activeCurrentTenantIndex === currentTenants.length - 1
            "
          >
            <template #top-content>
              <GenericH4>
                {{
                  isBrf
                    ? currentTenants.length > 1
                      ? activeCurrentTenant.index === 0
                        ? 'Bostadsrättshavaren'
                        : 'Medboende'
                      : 'Bostadsrättshavaren'
                    : currentTenants.length > 1
                      ? activeCurrentTenant.index === 0
                        ? 'Förstahandshyresgäst'
                        : 'Medhyresgäst'
                      : 'Förstahandshyresgästen'
                }}
              </GenericH4>
              <PersonCard
                :name="`${activeCurrentTenant.name} ${
                  activeCurrentTenant.protectedIdentity ? '❗️' : ''
                }`"
              >
                <!-- <template #name-icon>
                  <LegalIcon
                    v-if="activeCurrentTenantLegalRef"
                    color="#e00201"
                  />
                </template> -->
              </PersonCard>
              <MemberSelector
                v-if="currentTenants.length > 1"
                :items="
                  currentTenants.map((tenant, index) => ({
                    ...tenant,
                    label: tenant.name,
                    protectedIdentity: tenant.protectedIdentity
                    //legalRef: getLegalRef(application, tenant.ssn)
                  }))
                "
                :active-index="activeCurrentTenantIndex"
                @selectItemIndex="selectCurrentTenantIndex"
              />
            </template>
            <h3 class="name capitalize-first">
              {{ activeCurrentTenant.name }}
            </h3>
            <ul class="apartment-info">
              <li><b>Personnr.:</b> {{ activeCurrentTenant.ssn }}</li>
              <li><b>E-post:</b> {{ activeCurrentTenant.email }}</li>
              <li>
                <b>Telefonnr.:</b>
                {{ activeCurrentTenant.phone }}
              </li>
              <li v-if="onCurrentContractExist">
                <b>Står på hyreskontraktet:</b>
                {{ activeCurrentTenant.onCurrentContract ? 'Ja' : 'Nej' }}
              </li>
              <li
                v-if="!isIncoming && !isRemoved && activeCurrentTenant.scenario"
              >
                <b>Scenario:</b>
                <span v-html="formatScenario(activeCurrentTenant.scenario)" />
              </li>
            </ul>

            <template v-if="activeCurrentTenant.fullmaktName">
              <br />
              <h3 class="name capitalize-first">Fullmaktshavaren</h3>
              <ul class="apartment-info">
                <li>
                  <b>Namn:</b>
                  {{ activeCurrentTenant.fullmaktName }}
                </li>
                <li>
                  <b>Personnr.:</b>
                  {{ activeCurrentTenant.ssn }}
                </li>
                <li>
                  <b>E-post:</b>
                  {{ activeCurrentTenant.email }}
                </li>
                <li>
                  <b>Telefonnr.:</b>
                  {{ activeCurrentTenant.phone }}
                </li>
              </ul>
            </template>
            <template v-if="activeCurrentTenant.protectedIdentity">
              <br />
              <h4 class="protectedIdentity-color" style="line-height: 1.6">
                Skyddad identitet
              </h4>
              <p>
                <b>{{ activeCurrentTenant.name }}</b> har skyddad identitet
              </p>
            </template>
            <!-- <template v-if="activeCurrentTenantLegalRef">
              <br />
              <LegalRef
                :name="activeCurrentTenantLegalRef.name"
                :url="activeCurrentTenantLegalRef.url"
              />
            </template> -->
            <hr class="divider" />
            <SubletApplicationCheckboxes
              :files="application.files"
              :application="application"
              :application-id="application.id"
              :checked-files="application.checkedFiles"
              :single-docs-mode="!dualDocsMode"
              :other-checkboxes="otherCheckboxes"
              :application-status="application.landlordStatus"
              :is-current-tenant="true"
              @openFile="openDocumentModal($event)"
              @checksChange="updateCheckedMember($event)"
              @selectedComment="selectComment"
            />
            <hr class="divider" />
            <h3 class="name capitalize-first">
              {{ isBrf ? 'Bostadsrätten' : 'Lägenheten' }}
            </h3>
            <ul class="apartment-info">
              <li><b>Adress:</b> {{ apartment.address }}</li>
              <li><b>Postnr.:</b> {{ apartment.postalCode }}</li>
              <li><b>Ort:</b> {{ apartment.city }}</li>
              <li>
                <b>Objektnr./kontraktsnr.:</b> {{ apartment.objectNumber }}
              </li>
              <li><b>Lägenhetsnr.:</b> {{ apartment.apartmentNumber }}</li>
              <li><b>Nuvarande hyra:</b> {{ apartment.rent }}</li>
              <li>
                <b>Hyr ut lägenheten möblerad:</b>
                {{ application.apartment.furnished ? 'Ja' : 'Nej' }}
              </li>
            </ul>
            <hr class="divider" />
            <div class="card-content">
              <GenericH4>Skäl</GenericH4>
              <p>{{ getReasonText }}</p>
            </div>
            <template v-if="application.reason.text">
              <br />
              <GenericH4>Anledning</GenericH4>
              <p>{{ application.reason.text }}</p>
            </template>
            <template v-if="application.reason.type === 'other'">
              <hr class="divider" />
              <GenericH4>{{
                hasNewAddress
                  ? 'Adress under uthyrningstiden'
                  : 'Boendeort under uthyrningstiden'
              }}</GenericH4>
              <p>
                {{
                  hasNewAddress
                    ? application.reason.newAddress
                    : application.reason.city
                }}
              </p>
            </template>
            <template v-if="application.reason.type === 'utlandsvistelse'">
              <hr class="divider" />
              <GenericH4>{{
                hasNewAddress
                  ? 'Adress under uthyrningstiden'
                  : 'Boendeort under uthyrningstiden'
              }}</GenericH4>
              <p>
                {{
                  hasNewAddress
                    ? application.reason.newAddress
                    : application.reason.city
                }}
              </p>
            </template>

            <template
              v-if="
                application.reason.type === 'arbete' ||
                application.reason.type === 'studie'
              "
            >
              <hr class="divider" />
              <GenericH4>{{
                hasNewAddress
                  ? 'Adress under uthyrningstiden'
                  : 'Boendeort under uthyrningstiden'
              }}</GenericH4>
              <p>
                {{
                  hasNewAddress
                    ? application.reason.newAddress
                    : application.reason.city
                }}
              </p>
            </template>
            <template v-else-if="application.reason.type === 'sambo'">
              <hr class="divider" />
              <GenericH4>Adress under uthyrningstiden</GenericH4>
              <p v-if="hasNewAddress">
                {{ application.reason.newAddress }}
              </p>
              <p v-else>
                {{ application.reason.address }},
                {{ application.reason.postalCode }}
                {{ application.reason.city }}
              </p>
              <hr class="divider" />
              <GenericH4>Boendeform</GenericH4>
              <ul class="apartment-info">
                <li><b>Boendeform:</b> {{ housingTypeTitle }}</li>
                <li><b>Hyresvärd:</b> {{ application.reason.houseOwner }}</li>
                <li>
                  <b>Telefonnummer till hyresvärd:</b>
                  {{ application.reason.ownerPhone }}
                </li>
                <li>
                  <b>Email-adress till hyresvärd:</b>
                  {{ application.reason.ownerEmail }}
                </li>
              </ul>
              <hr class="divider" />
              <GenericH4>Uppgifter på den du ska provbo med</GenericH4>
              <ul class="apartment-info">
                <li><b>Namn:</b> {{ application.reason.partnerName }}</li>
                <li><b>Personnr.:</b> {{ application.reason.partnerSsn }}</li>
              </ul>
            </template>
          </AhOvApplicationCard>
          <AhOvApplicationCard
            :no-top-left-border="activeSecondaryTenantIndex === 0"
            :no-top-right-border="
              activeSecondaryTenantIndex === secondaryTenants.length - 1
            "
          >
            <template #top-content>
              <GenericH4>
                {{
                  isBrf
                    ? secondaryTenants.length > 1
                      ? activeSecondaryTenant.index === 0
                        ? 'Medboende 1'
                        : 'Medboende 2'
                      : 'Medboende'
                    : secondaryTenants.length > 1
                      ? activeSecondaryTenant.index === 0
                        ? 'Andrahandshyresgäst 1'
                        : 'Andrahandshyresgäst 2'
                      : 'Andrahandshyresgästen'
                }}
              </GenericH4>
              <PersonCard
                :name="`${activeSecondaryTenant.name} ${
                  activeSecondaryTenant.protectedIdentity ? '❗️' : ''
                }`"
              >
                <!-- <template #name-icon>
                  <LegalIcon
                    v-if="activeSecondaryTenantLegalRef"
                    color="#e00201"
                  />
                </template> -->
              </PersonCard>
              <MemberSelector
                v-if="secondaryTenants.length > 1"
                :items="
                  secondaryTenants.map((tenant, index) => ({
                    ...tenant,
                    label: tenant.name,
                    protectedIdentity: tenant.protectedIdentity
                    //legalRef: getLegalRef(application, tenant.ssn)
                  }))
                "
                :active-index="activeSecondaryTenantIndex"
                @selectItemIndex="selectSecondaryTenantIndex"
              />
            </template>

            <h3 class="name capitalize-first">
              {{ activeSecondaryTenant.name }}
            </h3>
            <ul class="apartment-info">
              <li><b>Personnr.:</b> {{ activeSecondaryTenant.ssn }}</li>
              <li><b>E-post:</b> {{ activeSecondaryTenant.email }}</li>
              <li>
                <b>Telefonnr.:</b>
                {{ activeSecondaryTenant.phone }}
              </li>
              <li><b>Adress:</b> {{ activeSecondaryTenant.address }}</li>
              <li
                v-if="
                  !isIncoming && !isRemoved && activeSecondaryTenant.scenario
                "
              >
                <b>Scenario:</b>
                <span v-html="formatScenario(activeSecondaryTenant.scenario)" />
              </li>
            </ul>
            <template v-if="activeSecondaryTenant.fullmaktName">
              <br />
              <h3 class="name capitalize-first">Fullmaktshavaren</h3>
              <ul class="apartment-info">
                <li>
                  <b>Namn:</b>
                  {{ activeSecondaryTenant.fullmaktName }}
                </li>
                <li>
                  <b>Personnr.:</b>
                  {{ activeSecondaryTenant.ssn }}
                </li>
                <li>
                  <b>E-post:</b>
                  {{ activeSecondaryTenant.email }}
                </li>
                <li>
                  <b>Telefonnr.:</b>
                  {{ activeSecondaryTenant.phone }}
                </li>
              </ul>
            </template>
            <template v-if="activeSecondaryTenant.protectedIdentity">
              <br />
              <GenericH4
                class="protectedIdentity-color"
                style="line-height: 1.6"
              >
                Skyddad identitet
              </GenericH4>
              <p>
                <b>{{ activeSecondaryTenant.name }}</b> har skyddad identitet
              </p>
            </template>
            <!-- <template v-if="activeSecondaryTenantLegalRef">
              <br />
              <LegalRef
                :name="activeSecondaryTenantLegalRef.name"
                :url="activeSecondaryTenantLegalRef.url"
              />
            </template> -->
            <hr class="divider" />
            <SubletApplicationCheckboxes
              v-if="dualDocsMode"
              :files="application.files"
              :application="application"
              :application-id="application.id"
              :single-docs-mode="!dualDocsMode"
              :checked-files="application.checkedFiles"
              :application-status="application.landlordStatus"
              :is-current-tenant="false"
              @openFile="openDocumentModal($event)"
              @checksChange="updateCheckedMember($event)"
              @selectedComment="selectComment"
            />
          </AhOvApplicationCard>
        </ApplicationsRow>
      </template>
      <!-- START ACTIVETAB 2 -->
      <template v-if="activeTab === 2">
        <div v-if="userHistory.length" class="grid">
          <div
            v-for="userApp in userHistory"
            :key="userApp.id"
            class="application-card-wrapper"
          >
            <SubletApplicationCard
              :application="userApp"
              :reason-type-title="reasonText(userApp.reason)"
              :is-new="false"
              :enable-link="currentApplicationId !== userApp.id"
              :full-name="userApp.assignedLandlordFirstName"
              :last-name="userApp.assignedLandlordLastName"
              status-date=""
              :show-tag-button="false"
              :show-days-left="false"
              :to-path="getToPath(userApp.id)"
              :show-chain-info="false"
            />
            <p v-if="currentApplicationId === userApp.id" class="is-same-card">
              <ChevronDownIcon />
              <span>Du har detta ärende öppet</span>
            </p>
          </div>
        </div>
        <div v-else>
          <h3 style="font-size: 2rem">
            Ingen historik finns ännu, denna användare har endast två länkade
            ärenden.
          </h3>
        </div>
      </template>
      <!-- END ACTIVETAB 2 -->
    </template>
    <template #footer>
      <FooterLeft>
        <AhOvAssignUser
          :application="application"
          @updateApplication="
            data => (application = { ...application, ...data })
          "
        />

        <button
          v-if="!isIncoming && !hasRemovalTypeStatus"
          class="simple-button notes-button"
          @click="currentModal = 'NOTES'"
        >
          <BaseIconText :icon-url="svgs.ICONS.NOTEBOOK" size="larger">
            <template #icon>
              <NotificationSymbol
                v-if="application.unreadNotes"
                border-color="#f1f2f6"
              />
            </template>
            Noteringar</BaseIconText
          >
        </button>

        <button
          v-if="!isIncoming && !hasRemovalTypeStatus"
          class="simple-button files-button"
          @click="currentModal = 'DOCUMENTS'"
        >
          <BaseIconText :icon-url="svgs.ICONS.ATTACHMENT" size="larger"
            >Visa {{ application.files?.length || 0 }} filer
          </BaseIconText>
        </button>
      </FooterLeft>
      <FooterRight>
        <div
          v-if="!isIncoming && !hasRemovalTypeStatus"
          class="popupModal-container"
        >
          <PopupModal
            v-if="popupModalVisible"
            :items="itemsInPopupModal"
            @close="popupModalVisible = false"
            @click="handlePopupClick"
          />
          <button
            class="simple-button notes-button"
            @click="popupModalVisible = !popupModalVisible"
          >
            <BaseIconText :icon-url="svgs.ICONS.MORE">Fler val</BaseIconText>
          </button>
        </div>
        <template v-if="application.landlordStatus === 'READY'">
          <BaseButton
            :is-warning="true"
            :is-rounded="true"
            :icon="svgs.ICONS.ERROR_WHITE"
            @click="currentModal = 'REJECT'"
          >
            Neka ansökan
          </BaseButton>

          <BaseButton
            :is-rounded="true"
            :icon="svgs.ICONS.CHECK_WHITE"
            @click="currentModal = 'APPROVE'"
          >
            Godkänn ansökan
          </BaseButton>
        </template>
        <BaseButton
          v-else-if="application.landlordStatus === 'APPROVED'"
          :is-warning="true"
          :is-rounded="true"
          :icon="svgs.ICONS.ERROR_WHITE"
          @click="currentModal = 'CLOSE'"
        >
          Avsluta uthyrning
        </BaseButton>
        <RejectReasonModal
          v-if="showRejectReasonModal"
          title="Nekad ansökan"
          :note="landlordRejectionNote"
          @close="rejectReasonModalVisible = false"
        />
      </FooterRight>
    </template>
  </ApplicationContainerModal>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import { addCheck, removeCheck } from '@/services/appService';
import { getAhApplication, getUserAhApplications } from '@/services/ahService';
import { startSession, stopSession } from '@/services/sessionService';
import svgs from '@/utils/icon-constants';
import { reasonText } from '@/utils/subletUtils';
import validDate from '@/utils/validDate';
import statusTypes, { readableStatusText } from '@/utils/statusTypes';
//import { getLegalRef } from '@/utils/legalRef';
import { APP_PATHS } from '@/utils/constants';

import BaseIconText from '@/components/BaseIconText.vue';
import BaseButton from '@/components/BaseButton.vue';
import TabHeader from '@/components/TabHeader.vue';
import NotificationSymbol from '@/components/NotificationSymbol.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import escClick from '@/directives/esc-click';
import PopupModal from '@/components/PopupModal.vue';
import ApplicationContainerModal from '@/components/ApplicationContainerModal.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import SubletNotesModal from '@/components/SubletNotesModal.vue';
import SubletDocumentsModal from '@/components/SubletDocumentsModal.vue';
import SubletApprovalModal from '@/components/SubletApprovalModal.vue';
import SubletRejectModal from '@/components/SubletRejectModal.vue';
import SubletCloseModal from '@/components/SubletCloseModal.vue';
import NotAssignedModal from '@/components/NotAssignedModal.vue';
import SubletRemoveModal from '@/components/SubletRemoveModal.vue';
import RestoreAhModal from '@/components/RestoreAhModal.vue';
import SubletApplicationCheckboxes from '@/components/SubletApplicationCheckboxes.vue';
import ApplicationsRow from './ApplicationsRow.vue';
import AhOvApplicationCard from '@/components/AhOvApplicationCard.vue';
import PersonCard from '@/components/PersonCard.vue';
import LinkChain from '@/components/LinkChain.vue';
import SubletApplicationCard from '@/components/SubletApplicationCard.vue';
import RejectReasonModal from '@/components/RejectReasonModal.vue';
import ChevronDownIcon from '@/components/Icons/ChevronDownIcon.vue';
import GenericH4 from '@/components/GenericH4.vue';
import AhOvAssignUser from '@/components/AhOvAssignUser.vue';
import MemberSelector from '@/components/MemberSelector.vue';
import CommentModal from '@/components/CommentModal.vue';
//import LegalIcon from '@/components/Icons/LegalIcon.vue';
//import LegalRef from '@/components/LegalRef.vue';
import FooterLeft from '@/components/FooterLeft.vue';
import FooterRight from '@/components/FooterRight.vue';
//import validDate from '@/utils/validDate';

export default {
  name: 'SubletApplicationView',
  directives: { escClick },
  components: {
    BaseIconText,
    BaseButton,
    SubletNotesModal,
    TabHeader,
    NotificationSymbol,
    ErrorModal,
    PopupModal,
    ApplicationContainerModal,
    BaseLoadingSpinner,
    SubletDocumentsModal,
    SubletApprovalModal,
    SubletRejectModal,
    NotAssignedModal,
    SubletRemoveModal,
    RestoreAhModal,
    SubletCloseModal,
    SubletApplicationCheckboxes,
    ApplicationsRow,
    AhOvApplicationCard,
    PersonCard,
    LinkChain,
    SubletApplicationCard,
    RejectReasonModal,
    ChevronDownIcon,
    GenericH4,
    AhOvAssignUser,
    MemberSelector,
    CommentModal,
    //LegalIcon,
    //LegalRef,
    FooterLeft,
    FooterRight
  },

  emits: ['close'],
  data() {
    return {
      svgs,
      selectedComment: null,
      currentModal: '',
      application: null,
      isAnimatingModalSize: false,
      isLoading: false,
      errorModalVisible: false,
      popupModalVisible: false,
      selectedApplicantId: null,
      selectedDocumentType: null,
      selectedDocumentCategoryTypes: [],
      userHistory: [],
      reasonText,
      readableStatusText,
      statusTypes,
      rejectReasonModalVisible: true,
      activeCurrentTenantIndex: 0,
      activeSecondaryTenantIndex: 0
      //getLegalRef
      //validDate
    };
  },
  computed: {
    ...mapState({
      ahApps: state => state.applications.ahApps,
      account: state => state.app.account
    }),
    ...mapGetters({
      ahAppsChains: 'applications/ahAppsChains',
      ahArchivedStatuses: 'applications/ahArchivedStatuses',
      ahPendingStatuses: 'applications/ahPendingStatuses'
    }),
    isBrf() {
      return this.application?.applicationType === 'andrahand-brf';
    },
    dualDocsMode() {
      // Originally only one person uploaded all files
      // Now there is a categoryType
      // If any categoryType is null it means its an older application
      return this.application.files.some(
        doc => doc.categoryType === 'secondary_tenant'
      );
    },
    onCurrentContractExist() {
      return Object.prototype.hasOwnProperty.call(
        this.activeCurrentTenant,
        'onCurrentContract'
      );
    },
    hasNewAddress() {
      return Object.prototype.hasOwnProperty.call(
        this.application?.reason || {},
        'newAddress'
      );
    },
    itemsInPopupModal() {
      const items = [
        // {
        //   title: 'Avbryt bytet',
        //   icon: svgs.ICONS.CHECK_NOTE_GREY,
        //   content:
        //     'Om en hyresgäst inte längre vill gå vidare med bytet, så flyttas ärendet till avslutat men markeras som avbrytet.',
        //   cta: 'Vill du avbryta bytet?',
        //   type: 'ABORT'
        // },
        {
          title: 'Radera ansökan',
          icon: `<svg viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                  d="M7.88232 16C8.14754 16 8.40189 15.8946 8.58943 15.7071C8.77697 15.5196 8.88232 15.2652 8.88232 15V9C8.88232 8.73478 8.77697 8.48043 8.58943 8.29289C8.40189 8.10536 8.14754 8 7.88232 8C7.61711 8 7.36275 8.10536 7.17522 8.29289C6.98768 8.48043 6.88232 8.73478 6.88232 9V15C6.88232 15.2652 6.98768 15.5196 7.17522 15.7071C7.36275 15.8946 7.61711 16 7.88232 16ZM17.8823 4H13.8823V3C13.8823 2.20435 13.5663 1.44129 13.0036 0.87868C12.441 0.316071 11.678 0 10.8823 0H8.88232C8.08667 0 7.32361 0.316071 6.761 0.87868C6.19839 1.44129 5.88232 2.20435 5.88232 3V4H1.88232C1.61711 4 1.36275 4.10536 1.17522 4.29289C0.987681 4.48043 0.882324 4.73478 0.882324 5C0.882324 5.26522 0.987681 5.51957 1.17522 5.70711C1.36275 5.89464 1.61711 6 1.88232 6H2.88232V17C2.88232 17.7956 3.19839 18.5587 3.761 19.1213C4.32361 19.6839 5.08667 20 5.88232 20H13.8823C14.678 20 15.441 19.6839 16.0036 19.1213C16.5663 18.5587 16.8823 17.7956 16.8823 17V6H17.8823C18.1475 6 18.4019 5.89464 18.5894 5.70711C18.777 5.51957 18.8823 5.26522 18.8823 5C18.8823 4.73478 18.777 4.48043 18.5894 4.29289C18.4019 4.10536 18.1475 4 17.8823 4ZM7.88232 3C7.88232 2.73478 7.98768 2.48043 8.17522 2.29289C8.36275 2.10536 8.61711 2 8.88232 2H10.8823C11.1475 2 11.4019 2.10536 11.5894 2.29289C11.777 2.48043 11.8823 2.73478 11.8823 3V4H7.88232V3ZM14.8823 17C14.8823 17.2652 14.777 17.5196 14.5894 17.7071C14.4019 17.8946 14.1475 18 13.8823 18H5.88232C5.61711 18 5.36275 17.8946 5.17522 17.7071C4.98768 17.5196 4.88232 17.2652 4.88232 17V6H14.8823V17ZM11.8823 16C12.1475 16 12.4019 15.8946 12.5894 15.7071C12.777 15.5196 12.8823 15.2652 12.8823 15V9C12.8823 8.73478 12.777 8.48043 12.5894 8.29289C12.4019 8.10536 12.1475 8 11.8823 8C11.6171 8 11.3628 8.10536 11.1752 8.29289C10.9877 8.48043 10.8823 8.73478 10.8823 9V15C10.8823 15.2652 10.9877 15.5196 11.1752 15.7071C11.3628 15.8946 11.6171 16 11.8823 16Z"
                  fill="#878b9d" />
          </svg>`,
          content:
            'Vill du ta bort hela ansökan och dess information, från tjänsten helt och hållet?',
          cta: 'Vill du radera ansökan?',
          type: 'REMOVE',
          theme: 'red'
        }
      ];
      if (
        this.application.landlordStatus !== 'READY' &&
        !this.hasRemovalTypeStatus
      ) {
        items.push({
          title: 'Återställ ansökan',
          icon: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.6863 15.3137 6 12 6C8.68629 6 6 8.6863 6 12V14.5" stroke="#878b9d"
                  stroke-width="2" stroke-linecap="round" />
              <path d="M6 15L3 12" stroke="#878b9d" stroke-width="2" stroke-linecap="round" />
              <path d="M6 15L9 12" stroke="#878b9d" stroke-width="2" stroke-linecap="round" />
          </svg>`,
          content: 'Vill du flytta tillbaka ansökan till "Fatta beslut"?',
          cta: 'Återställ ansökan',
          theme: 'blue',
          type: 'RESTORE'
        });
      }
      return items;
    },
    showRejectReasonModal() {
      return this.isRejected && this.rejectReasonModalVisible;
    },
    landlordRejectionNote() {
      return this.application?.landlordRejectionNote;
    },
    activeTabs() {
      return [
        {
          title: 'Andrahandsansökan',
          active: this.activeTab === 1,
          tabLink: 1
        },
        {
          title: `<svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              fill="#fff"
              d="M6 1a1 1 0 0 0-2 0h2ZM4 4a1 1 0 0 0 2 0H4Zm7-3a1 1 0 1 0-2 0h2ZM9 4a1 1 0 1 0 2 0H9Zm7-3a1 1 0 1 0-2 0h2Zm-2 3a1 1 0 1 0 2 0h-2ZM1 6a1 1 0 0 0 0 2V6Zm18 2a1 1 0 1 0 0-2v2ZM5 11v-1H4v1h1Zm0 .01H4v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM10 11v-1H9v1h1Zm0 .01H9v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM10 15v-1H9v1h1Zm0 .01H9v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM15 15v-1h-1v1h1Zm0 .01h-1v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM15 11v-1h-1v1h1Zm0 .01h-1v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM5 15v-1H4v1h1Zm0 .01H4v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM2 4h16V2H2v2Zm16 0h2a2 2 0 0 0-2-2v2Zm0 0v14h2V4h-2Zm0 14v2a2 2 0 0 0 2-2h-2Zm0 0H2v2h16v-2ZM2 18H0a2 2 0 0 0 2 2v-2Zm0 0V4H0v14h2ZM2 4V2a2 2 0 0 0-2 2h2Zm2-3v3h2V1H4Zm5 0v3h2V1H9Zm5 0v3h2V1h-2ZM1 8h18V6H1v2Zm3 3v.01h2V11H4Zm1 1.01h.01v-2H5v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H5v2h.01v-2ZM9 11v.01h2V11H9Zm1 1.01h.01v-2H10v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H10v2h.01v-2ZM9 15v.01h2V15H9Zm1 1.01h.01v-2H10v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H10v2h.01v-2ZM14 15v.01h2V15h-2Zm1 1.01h.01v-2H15v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H15v2h.01v-2ZM14 11v.01h2V11h-2Zm1 1.01h.01v-2H15v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H15v2h.01v-2ZM4 15v.01h2V15H4Zm1 1.01h.01v-2H5v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H5v2h.01v-2Z"
            ></path>
          </svg>Historik`,
          active: this.activeTab === 2,
          tabLink: 2
        }
      ];
    },
    secondaryTenants() {
      return this.application.secondaryTenants;
    },
    currentTenants() {
      return this.application.currentTenants;
    },
    isRemoved() {
      return this.application.landlordStatus === this.statusTypes.REMOVED;
    },
    isPendingRemoval() {
      return (
        this.application.landlordStatus === this.statusTypes.PENDING_REMOVE
      );
    },
    hasRemovalTypeStatus() {
      return this.isRemoved || this.isPendingRemoval;
    },
    applicantName() {
      if (
        !this.selectedDocumentCategoryTypes?.length ||
        this.selectedDocumentCategoryTypes.includes('current_tenant') ||
        this.selectedDocumentCategoryTypes.includes(null)
      ) {
        return this.application.currentTenants[0].name;
      }
      return this.application.secondaryTenants[0].name;
    },
    activeCurrentTenant() {
      return (
        this.application.currentTenants[this.activeCurrentTenantIndex] || {}
      );
    },
    // activeCurrentTenantLegalRef() {
    //   const legalRefUrl = this.getLegalRef(
    //     this.application,
    //     this.activeCurrentTenant.ssn
    //   );
    //   if (!legalRefUrl) {
    //     return null;
    //   }
    //   return {
    //     name: this.activeCurrentTenant.name,
    //     url: legalRefUrl
    //   };
    // },
    activeSecondaryTenant() {
      return (
        this.application.secondaryTenants[this.activeSecondaryTenantIndex] || {}
      );
    },
    // activeSecondaryTenantLegalRef() {
    //   const legalRefUrl = this.getLegalRef(
    //     this.application,
    //     this.activeSecondaryTenant.ssn
    //   );
    //   if (!legalRefUrl) {
    //     return null;
    //   }
    //   return {
    //     name: this.activeSecondaryTenant.name,
    //     url: legalRefUrl
    //   };
    // },
    currentApplicationId() {
      const { id } = this.$route.params;
      return Number(id);
    },
    activeTab() {
      return this.$route.query.tab ? parseInt(this.$route.query.tab) : 1;
    },
    getReasonText() {
      return this.reasonText(this.application.reason);
    },
    tabHeaderSubtitle() {
      return this.readableStatusText(this.application.landlordStatus) || '';
    },
    isArchived() {
      return this.ahArchivedStatuses.includes(this.application.landlordStatus);
    },
    isIncoming() {
      return this.application.landlordStatus === this.statusTypes.INCOMING;
    },
    isRejected() {
      return this.application.landlordStatus === this.statusTypes.REJECTED;
    },
    apartment() {
      return this.application.apartment;
    },
    otherCheckboxes() {
      const { rentFrom, rentTo } = this.application.reason;
      return [
        {
          category: 'onskat_datum',
          value:
            validDate(rentFrom) && validDate(rentFrom)
              ? `${new Date(rentFrom).toLocaleDateString(
                  'sv-SE'
                )} till ${new Date(rentTo).toLocaleDateString('sv-SE')}`
              : '-'
        }
      ];
    },
    housingTypeTitle() {
      if (this.application.reason.housingType === 'hyresratt') {
        return 'Hyresrätt';
      } else if (this.application.reason.housingType === 'bostadsratt') {
        return 'Bostadsrätt';
      } else {
        return 'Villa';
      }
    }
  },
  watch: {
    currentApplicationId: {
      async handler(newId, prevId) {
        if (newId !== prevId) {
          this.isLoading = true;
          try {
            await this.getSubletApplication();
            await Promise.all([
              this.getFiles(this.currentApplicationId),
              this.getUserHistory()
            ]);
          } catch {
            this.showErrorModal();
          }
          this.isLoading = false;
          startSession(this.currentApplicationId, 'andrahand');
        }
      },
      immediate: true
    },
    activeTab: {
      async handler(activeTab) {
        if (activeTab === 2 && this.application) {
          await this.getUserHistory();
        }
      },
      immediate: true
    }
  },
  beforeUnmount() {
    stopSession();
  },
  methods: {
    ...mapMutations({
      ahOvApplicationReadNotes: 'applications/ahOvApplicationReadNotes'
    }),
    ...mapActions({
      getFiles: 'landlordFiles/getFiles'
    }),
    formatScenario(scenario) {
      // Replace RegEx with <br>
      return scenario ? scenario.replace(/\n/g, '<br>') : '-';
    },
    selectSecondaryTenantIndex(index) {
      this.activeSecondaryTenantIndex = index;
    },
    selectCurrentTenantIndex(index) {
      this.activeCurrentTenantIndex = index;
    },
    selectComment(comment) {
      this.selectedComment = {
        name: comment.isCurrentTenant
          ? this.isBrf
            ? 'Bostadsrättshavaren'
            : 'Uthyraren'
          : this.isBrf
            ? 'Medboende'
            : 'Andrahandshyresgästen',
        documentType: comment.documentType,
        content: comment.content
      };
    },
    getToPath(id) {
      const currentPath = this.$router.currentRoute.value.path;

      const applicationId = Number(id);

      // Here we try to find the correct path
      // We don't fetch all apps initally depending on which view is loaded
      // Initially until the other view is loaded we have to make assumptions
      // If we cant find the app we assume it's on the other view

      if (currentPath.includes(`${APP_PATHS.ANDRAHAND}/alla`)) {
        if (
          this.ahApps.find(
            app =>
              app.id === applicationId &&
              this.ahPendingStatuses.includes(app.landlordStatus)
          )
        ) {
          return `/${APP_PATHS.ANDRAHAND}/alla`;
        } else {
          return `/${APP_PATHS.ANDRAHAND}/arkiv`;
        }
      }

      if (currentPath.includes(`${APP_PATHS.ANDRAHAND}/arkiv`)) {
        if (
          this.ahApps.find(
            app =>
              app.id === applicationId &&
              this.ahArchivedStatuses.includes(app.landlordStatus)
          )
        ) {
          return `/${APP_PATHS.ANDRAHAND}/arkiv`;
        } else {
          return `/${APP_PATHS.ANDRAHAND}/alla`;
        }
      }

      // Fallback
      return `/${APP_PATHS.ANDRAHAND}/alla`;
    },
    async getUserHistory() {
      const userApps = await getUserAhApplications(this.application.userId);
      this.userHistory = userApps;
    },
    leaveApplication() {
      this.$router.push({ path: '/andrahand/alla' });
    },
    async updateCheckedMember({ checked, category }) {
      try {
        if (checked) {
          await addCheck(this.application.id, 0, category);
          const addedChecks = [...this.application.checkedFiles, category];

          this.application.checkedFiles = addedChecks;
        } else {
          await removeCheck(this.application.id, 0, category);
          const removedChecks = this.application.checkedFiles.filter(
            check => check !== category
          );

          this.application.checkedFiles = removedChecks;
        }
      } catch (error) {
        console.error(error);
      }
    },
    openDocumentModal({ index, category, categoryTypes }) {
      if (this.isIncoming) {
        return;
      } else {
        this.selectedApplicantId = `${index}`;
        this.selectedDocumentType = category;
        this.selectedDocumentCategoryTypes = categoryTypes;
        this.currentModal = 'DOCUMENTS';
      }
    },
    showErrorModal() {
      this.errorModalVisible = true;
    },
    async getSubletApplication() {
      const res = await getAhApplication(this.currentApplicationId);
      this.application = res;
    },
    readNotes() {
      this.application.unreadNotes = 0;
      this.ahOvApplicationReadNotes({
        applicationId: this.application.id,
        isAh: true
      });
    },
    setTab(tabNumber) {
      this.$router.push({ query: { tab: tabNumber } });
    },
    handleEscClick() {
      if (!this.currentModal) {
        this.$emit('close');
      }
    },
    handlePopupClick(type) {
      switch (type) {
        case 'REMOVE':
          if (this.currentUserIsAssigned) {
            this.currentModal = 'REMOVE';
          } else {
            this.currentModal = 'USER_NOT_ASSIGNED';
          }
          break;
        case 'ABORT':
          console.log('Not implemented: Show ABORT modal');
          break;
        case 'RESTORE':
          this.currentModal = 'RESTORE';
          break;
        default:
          this.currentModal = null;
      }
      this.popupModalVisible = false;
    }
  }
};
</script>

<style scoped>
.name {
  color: var(--color-dark-blue);
  font-weight: 600;
  font-size: 1.8rem;
}
.name-small {
  color: var(--color-dark-blue);
}

.apartment-info li {
  font-weight: 400;
  font-size: 12px;
  line-height: 1.6;
}
.apartment-info li b {
  font-weight: 600;
}

.divider {
  background-color: #e4e5e8;
  margin: 2rem 0;
  border: none;
  height: 1px;
}

.base-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-shadow: 0px 2px 15px rgba(0, 0, 0, 0.05);
  border-radius: 12px;
  overflow: hidden;
}
.body {
  position: relative;
  background: #fff;
  padding: 25px 35px 50px 35px;
  width: 100%;
  overflow: auto;
  max-height: 70vh;
  overflow-x: hidden;
}
.body > :deep(.applications-row) {
  opacity: 1;
  transition: opacity 300ms ease-in-out;
}
.body--animating {
  overflow: hidden;
}
.body--animating > :deep(.applications-row) {
  opacity: 0;
  transition: none;
}
.footer {
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: #fff;
  align-items: center;
  padding: 22px 35px 22px 35px;
  background: #f1f2f6;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}

.loading-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}
.pdf-link {
  margin-top: 30px;
  font-size: 12px;
  text-decoration: underline;
}
.assign-button,
.notes-button,
.files-button {
  font-weight: 600;
  font-size: 1.2rem;
}
.assign-button {
  color: var(--color-dark-blue);
  font-weight: 700;
}
.popupModal-container {
  position: relative;
  vertical-align: text-bottom;
  display: inline-block;
}

.link-chain {
  background-color: #fff;
  padding: 0.4rem;
  width: 3.8rem;
  height: 3.8rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;
}

.application-card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  width: 260px;
}

.is-same-card {
  border-radius: 10px;
  border-bottom-left-radius: 0;
  padding: 3px;
  border-bottom-right-radius: 0;
  border-bottom: 0;
  color: #000;
  font-weight: 400;
  font-size: 16px;
  text-align: center;
  font-family: 'Averta Std';
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.is-same-card svg {
  transform: rotate(180deg);
  width: 1.4rem;
  height: 1.4rem;
}
</style>
